import type { HTTPParams } from "@shared/types/HTTP";
import Flash from "@shared/services/flashv2";
import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
import { showPreviewForbiddenFlash } from "@retailer/services/preview_forbidden_flash";
import GitRevisionCheck from "@shared/services/git_revision_check_modern";
import { get } from "svelte/store";
import * as Sentry from "@sentry/browser";

declare const window: any;

function getXsrfToken() {
  let cookies: string[][] = document.cookie.split("; ").map(c => c.split("="));

  const xsrfRawCookie: string[] = cookies.find(c => c[0] == "XSRF-TOKEN");

  let xsrfToken: string;
  if (xsrfRawCookie) {
    xsrfToken = decodeURIComponent(xsrfRawCookie[1]);
  }
  return xsrfToken;
}

function buildSearchParameters(params) {
  return Object.keys(params)
    .map(function (k) {
      let data = params[k];
      let key = encodeURIComponent(k);

      if (Array.isArray(data)) {
        return data
          .map(v => {
            return key + "[]=" + encodeURIComponent(v);
          })
          .join("&");
      } else if (data || data === false) {
        return key + "=" + encodeURIComponent(data);
      } else {
        return [];
      }
    })
    .join("&");
}

async function makeHttpRequest(params: HTTPParams, defaultToJson: boolean = true) {
  let toSend: RequestInit = {
    method: params.method,
    headers: {},
    credentials: params.credentials || "include",
  };

  if (params.method != "GET") {
    toSend.headers["X-XSRF-TOKEN"] = getXsrfToken();
    if (defaultToJson && !(params.url == "/api/distributor/image_uploads")) {
      (toSend.headers["Content-Type"] = "application/json; charset=utf-8"),
        (toSend.body = JSON.stringify(params.data));
    } else {
      toSend.body = params.data;
    }
  } else if (params.data) {
    params.url += `?${buildSearchParameters(params.data)}`;
  }

  if (window["$rootScope"] && window["$rootScope"].currentRetailerId) {
    toSend.headers["X-Tiz-Retailer-Context"] = String(window["$rootScope"].currentRetailerId);
  }
  if (window["$rootScope"] && window["$rootScope"].currentDistributor) {
    toSend.headers["X-Tiz-Distributor-Context"] = String(window["$rootScope"].currentDistributor);
  }

  let customHeaders = params.headers || {};
  for (let headerName in customHeaders) {
    if (!customHeaders.hasOwnProperty(headerName)) {
      continue;
    }
    if (customHeaders[headerName] !== undefined) {
      toSend.headers[headerName] = customHeaders[headerName];
    } else {
      delete toSend.headers[headerName];
    }
  }

  window.pendingHttpRequests = 1 + (window.pendingHttpRequests || 0);

  return await fetch(params.url, toSend);
}

export async function Http(params: HTTPParams, cb, errCb) {
  const originalParams = { ...params };
  let res;

  try {
    res = await makeHttpRequest(params);
  } catch (e) {
    if (e instanceof TypeError) {
      if (errCb) {
        return errCb(e);
      }
    } else {
      throw e;
    }
  }

  if (!res || res.headers === undefined) {
    return false;
  }

  let contentType: string[] = res.headers.get("Content-Type")?.split(";") || [];
  let hasJson: boolean = contentType.includes("application/json");
  let json;

  if (res.ok) {
    if (window["modern_mode"]) {
      GitRevisionCheck.setRevisionStatus(res.headers.get("x-tiz-git-revision"));
    }
    if (hasJson) {
      json = await res.json();
    }
    window.pendingHttpRequests--;
    return cb(json, res);
  } else {
    let body;
    if (hasJson) {
      body = await res.json();
    }
    if (res.status == 401) {
      if (body && body.error == "Authentication required") {
        window.location.href = "/";
      }
    }
    if (res.status === 403) {
      if (window.C.APP_NAME == "distributor") {
        if (params.handle403Callback) {
          params.handle403Callback(res);
        } else {
          Flash.now({
            type: "error",
            header: "Access denied",
            message: "This page could not be accessed.",
          });
        }
      } else {
        if (get(CurrentUserStore)?.role == "supplier" && !window.isChangingState) {
          showPreviewForbiddenFlash();
        }
      }
    }

    window.pendingHttpRequests--;

    if (!errCb) return res;
    if (hasJson) return errCb(body);

    const error = new ApiError(`${res.status} ${originalParams.method} - ${originalParams.url}`);
    error.status = res.status;

    sendApiErrorToSentry({ error, status: res.status, params: originalParams });
    return errCb(error);
  }
}

const resolve = attrs => {
  return Promise.resolve(attrs);
};
const fail = attrs => {
  return Promise.reject(attrs);
};
export function HttpPromise(params: HTTPParams) {
  return Http(params, resolve, fail);
}
export function HttpPromiseWithEntireResponse(params: HTTPParams) {
  return makeHttpRequest(params);
}
export function CustomHttpPromiseWithEntireResponse(params: HTTPParams) {
  return makeHttpRequest(params, false);
}

export class ApiError extends Error {
  status?: number;

  constructor(...args) {
    super(...args);
    this.name = "ApiError";
  }
}

export const sendApiErrorToSentry = ({
  error,
  status,
  params,
}: {
  error: Error;
  status: string | number;
  params: HTTPParams;
}) => {
  if (!Sentry) return;

  status = Number(status);
  if ([400, 401, 403, 422].includes(status)) return;

  Sentry.withScope(scope => {
    scope.setExtra("context", window.sentryContext);
    scope.setExtra("requestData", params);

    const url = params.url || "";
    const urlWithoutIdsOrDoubleSlashes = url.replace(/\d+/, ":id").replace(/\/{2,}/, "");

    // Sentry seems to think that nearly every API error is unique and special in its own way
    // This encourages Sentry to group all instances of a given error like "500 POST /api/cart/submit" as the same error
    scope.setFingerprint([String(status), params.method, urlWithoutIdsOrDoubleSlashes]);

    Sentry.captureException(error);
  });
};
